import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Clock, RefreshCw, ArrowRight } from 'lucide-react';
import axios from '../api/api';
import toast from 'react-hot-toast';
import { fetchExchangeRates } from '../utils/currencyUtils';

const EmbedlyPayment = ({ price, orderId, customerName, fromStore }) => {
    const navigate = useNavigate();
    const [checkoutWallet, setCheckoutWallet] = useState(null);
    const [timeLeft, setTimeLeft] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const [amountNGN, setAmountNGN] = useState(0);
    const [showDetails, setShowDetails] = useState(false);

    // Fetch exchange rates and convert to NGN
    useEffect(() => {
        // Since price is already in NGN (from Cart/Shipping logic), we don't need to convert from USD
        setAmountNGN(price);
    }, [price]);

    // Check for pending checkout on mount
    useEffect(() => {
        const checkForPendingCheckout = async () => {
            try {
                const { data } = await axios.get(`/payment/embedly/order-checkout-status?orderId=${orderId}`, {
                    withCredentials: true,
                });

                if (data.success && data.checkout) {
                    console.log('Found existing order checkout:', data.checkout);
                    setCheckoutWallet({
                        id: data.checkout.walletId,
                        walletNumber: data.checkout.walletNumber,
                        walletName: data.checkout.walletName,
                        checkoutRef: data.checkout.checkoutRef,
                        expectedAmount: data.checkout.expectedAmount,
                        originalAmount: data.checkout.originalAmount,
                        originalCurrency: data.checkout.originalCurrency,
                        expiresAt: data.checkout.expiresAt
                    });
                    setShowDetails(true);
                    toast.info('Restored your pending payment. Please complete the transfer.');
                }
            } catch (error) {
                console.error('Error checking for pending checkout:', error);
            }
        };

        if (orderId && amountNGN > 0) {
            checkForPendingCheckout();
        }
    }, [orderId, amountNGN]);

    // Timer for wallet expiry
    useEffect(() => {
        if (checkoutWallet && checkoutWallet.expiresAt) {
            const interval = setInterval(() => {
                const now = new Date().getTime();
                const expiryTime = new Date(checkoutWallet.expiresAt).getTime();
                const difference = expiryTime - now;

                if (difference > 0) {
                    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
                    const seconds = Math.floor((difference % (1000 * 60)) / 1000);
                    setTimeLeft(`${minutes}m ${seconds}s`);
                } else {
                    setTimeLeft('Expired');
                    clearInterval(interval);
                }
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [checkoutWallet]);

    const createCheckoutWallet = async () => {
        if (amountNGN === 0) {
            toast.error('Please wait for currency conversion to complete');
            return;
        }

        setIsLoading(true);
        try {
            const { data } = await axios.post(
                '/payment/embedly/create-order-checkout',
                {
                    orderId: orderId,
                    amountNGN: amountNGN,
                    originalAmount: price,
                    originalCurrency: 'NGN'
                },
                { withCredentials: true }
            );

            if (data.success) {
                setCheckoutWallet(data.data);
                setShowDetails(true);
                toast.success('Payment account created! Please make a bank transfer.');
            } else {
                toast.error(data.error || 'Failed to create payment wallet');
            }
        } catch (error) {
            console.error('Error creating checkout wallet:', error);

            // Check if there's an existing checkout
            if (error.response?.data?.existingCheckout) {
                setCheckoutWallet(error.response.data.existingCheckout);
                setShowDetails(true);
                toast.info('You already have a pending payment for this order.');
            } else {
                toast.error(error.response?.data?.error || 'Failed to create payment wallet');
            }
        }
        setIsLoading(false);
    };

    const checkPaymentStatus = async () => {
        if (!checkoutWallet) return;

        setIsChecking(true);
        try {
            const { data } = await axios.post(
                '/payment/embedly/verify-order-payment',
                {
                    walletId: checkoutWallet.id,
                    orderId: orderId
                },
                { withCredentials: true }
            );

            if (data.success) {
                toast.success(data.message || 'Payment successful! Your order has been confirmed.');
                setCheckoutWallet(null);
                setShowDetails(false);
                // Navigate to order received page
                const orderReceivedPath = fromStore ? `/${fromStore}/order-received` : '/order-received';
                setTimeout(() => navigate(orderReceivedPath, { state: { fromStore } }), 1500);
            } else {
                toast.error(data.error || 'Payment verification failed');
            }
        } catch (error) {
            console.error('Error verifying payment:', error);

            // Check if it's a reversed transaction error
            if (error.response?.data?.walletUnusable) {
                toast.error(error.response.data.error, { duration: 6000 });
                clearCheckout('reversed');
            } else {
                toast.error(error.response?.data?.error || 'Failed to verify payment');
            }
        } finally {
            setIsChecking(false);
        }
    };

    const clearCheckout = async (reason = 'user_cancelled') => {
        try {
            const { data } = await axios.post(
                '/payment/embedly/clear-order-checkout',
                {
                    walletId: checkoutWallet?.id,
                    orderId,
                    reason: reason
                },
                { withCredentials: true }
            );

            if (data.success) {
                setCheckoutWallet(null);
                setShowDetails(false);
                if (reason === 'reversed') {
                    toast.info('Payment account cleared. Please generate a new one.');
                } else {
                    toast.success('Payment cancelled successfully.');
                }
            }
        } catch (error) {
            console.error('Error clearing checkout:', error);
            // Even if API fails, clear locally
            setCheckoutWallet(null);
            setShowDetails(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard!');
    };

    if (showDetails && checkoutWallet) {
        return (
            <div className="mt-6">
                <div className="rounded-lg overflow-hidden">
                    {/* Header with Timer */}
                    <div className="bg-[var(--store-card-bg)] px-5 py-3 flex items-center justify-between">
                        <span className="text-sm text-[var(--store-text-secondary)]">Complete payment within</span>
                        <div className="flex items-center gap-2">
                            <Clock className="text-[var(--store-text-secondary)]" size={16} />
                            <span className="text-sm font-medium text-[var(--store-text)]">{timeLeft}</span>
                        </div>
                    </div>

                    {/* Payment Details */}
                    <div className="bg-[var(--store-bg)] px-5 py-6 space-y-5">
                        {/* Amount */}
                        <div>
                            <label className="text-xs text-[var(--store-text-muted)] uppercase tracking-wide">Amount</label>
                            <div className="mt-1">
                                <p className="text-2xl font-semibold text-[var(--store-text)]">NGN {checkoutWallet.expectedAmount.toLocaleString()}</p>
                            </div>
                        </div>

                        {/* Account Number */}
                        <div>
                            <label className="text-xs text-[var(--store-text-muted)] uppercase tracking-wide">Account Number</label>
                            <div className="mt-2 flex items-center justify-between bg-[var(--store-card-bg)] rounded px-4 py-3">
                                <span className="text-lg font-mono text-[var(--store-text)]">{checkoutWallet.walletNumber}</span>
                                <button
                                    onClick={() => copyToClipboard(checkoutWallet.walletNumber)}
                                    className="text-[var(--store-text-secondary)] hover:text-[var(--store-text)] transition-colors"
                                    title="Copy account number"
                                >
                                    <Copy size={18} />
                                </button>
                            </div>
                        </div>

                        {/* Account Name */}
                        <div>
                            <label className="text-xs text-[var(--store-text-muted)] uppercase tracking-wide">Account Name</label>
                            <div className="mt-2 bg-[var(--store-card-bg)] rounded px-4 py-3">
                                <span className="text-sm text-[var(--store-text)]">{checkoutWallet.walletName}</span>
                            </div>
                        </div>

                        {/* Bank */}
                        <div>
                            <label className="text-xs text-[var(--store-text-muted)] uppercase tracking-wide">Bank</label>
                            <div className="mt-2 bg-[var(--store-card-bg)] rounded px-4 py-3">
                                <span className="text-sm text-[var(--store-text)]">Sterling Bank</span>
                            </div>
                        </div>

                        {/* Reference (collapsible/minimal) */}
                        <div>
                            <label className="text-xs text-[var(--store-text-muted)] uppercase tracking-wide">Reference</label>
                            <div className="mt-2 flex items-center justify-between bg-[var(--store-card-bg)] rounded px-4 py-3">
                                <span className="text-xs font-mono text-[var(--store-text-secondary)]">{checkoutWallet.checkoutRef}</span>
                                <button
                                    onClick={() => copyToClipboard(checkoutWallet.checkoutRef)}
                                    className="text-[var(--store-text-secondary)] hover:text-[var(--store-text)] transition-colors"
                                    title="Copy reference"
                                >
                                    <Copy size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Info Notice */}
                        <div className="pt-2">
                            <p className="text-xs text-[var(--store-text-muted)]">
                                Transfer exactly <span className="text-[var(--store-text)] font-medium">NGN {checkoutWallet.expectedAmount.toLocaleString()}</span> to this account. Payment will be confirmed automatically.
                            </p>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="bg-[var(--store-card-bg)] px-5 py-4 flex gap-3">
                        <button
                            onClick={checkPaymentStatus}
                            disabled={isChecking || timeLeft === 'Expired'}
                            className="flex-1 px-6 py-2.5 rounded-md text-[var(--store-bg)] bg-[var(--store-text)] hover:opacity-90 font-semibold transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                            {isChecking ? (
                                <>
                                    <RefreshCw className="mr-2 animate-spin" size={16} />
                                    Verifying...
                                </>
                            ) : (
                                "I've completed the transfer"
                            )}
                        </button>

                        <button
                            onClick={() => clearCheckout('user_cancelled')}
                            disabled={isChecking}
                            className="px-5 py-2.5 rounded-md text-[var(--store-text-secondary)] hover:text-[var(--store-text)] bg-[var(--store-bg)] hover:bg-[var(--store-card-bg)] transition-all disabled:opacity-50 text-sm"
                        >
                            Cancel
                        </button>
                    </div>

                    {/* Expired State */}
                    {timeLeft === 'Expired' && (
                        <div className="bg-[var(--store-card-bg)] px-5 py-3">
                            <p className="text-sm text-red-400 mb-3">This payment account has expired</p>
                            <button
                                onClick={() => clearCheckout('expired')}
                                className="w-full px-6 py-2.5 rounded-md bg-red-600 text-white hover:bg-red-700 font-semibold transition-all text-sm"
                            >
                                Generate New Account
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="mt-4">
            {amountNGN > 0 ? (
                <div className="mb-4 p-4 bg-[var(--store-card-bg)] rounded-lg">
                    <p className="text-sm text-[var(--store-text-secondary)] mb-2">Payment Amount</p>
                    <p className="text-3xl font-bold text-[var(--store-accent)]">NGN {amountNGN.toLocaleString()}</p>
                </div>
            ) : (
                <div className="mb-4 p-4 bg-[var(--store-card-bg)] rounded-lg">
                    <p className="text-sm text-[var(--store-text-secondary)]">Converting currency...</p>
                </div>
            )}

            <button
                onClick={createCheckoutWallet}
                disabled={isLoading || amountNGN === 0}
                className="px-10 py-[6px] rounded-sm text-[var(--store-bg)] bg-[var(--store-text)] hover:opacity-90 font-bold transition-all flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {isLoading ? (
                    <>
                        <RefreshCw className="mr-2 animate-spin" size={18} />
                        Generating Payment Account...
                    </>
                ) : (
                    <>
                        Pay with Bank Transfer
                        <ArrowRight className="ml-3" size={18} />
                    </>
                )}
            </button>
        </div>
    );
};

export default EmbedlyPayment;
